import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CloudUpload,
  PlayArrow,
  CheckCircle,
  Description,
  Download,
  Clear,
  Image as ImageIcon,
  ColorLens,
  Style,
  Close as CloseIcon,
  ZoomIn,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import HelpInfoIcon from '../components/HelpInfoIcon';
import { API_ENDPOINTS, apiCall } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const ProductDetails = () => {
  const theme = useTheme();
  const { processes, addProcess, updateProcess, removeProcess } = useProcess();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [file, setFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [productType, setProductType] = useState('rugs');
  const [processing, setProcessing] = useState(false);
  const [processId, setProcessId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [styleResult, setStyleResult] = useState(null);
  const [colorProcessingMode, setColorProcessingMode] = useState('sku'); // 'sku', 'color', 'image'
  const [imageModalOpen, setImageModalOpen] = useState(false);

  // Test API connection on mount
  useEffect(() => {
    testConnection();
  }, []);

  // Poll for status when processing
  useEffect(() => {
    if (processId && processing) {
      intervalRef.current = setInterval(() => {
        fetchStatus();
      }, 1000);

      // Store interval in context for cleanup
      setIntervalRef(processId, intervalRef.current);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [processing, processId]);

  // Test API connection
  const testConnection = async () => {
    try {
      const data = await apiCall(API_ENDPOINTS.PRODUCT_DETAILS_TEST, {
        method: 'GET',
      });

      if (!data.success) {
        setError('API configuration issue. Please contact administrator.');
      }
    } catch (err) {
      console.error('Error testing connection:', err);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetImage(selectedFile);
    }
  };

  // Validate file
  const validateAndSetFile = (selectedFile) => {
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      setError('Please select a valid Excel file (.xlsx, .xls) or CSV file');
      return;
    }

    if (selectedFile.size > 50 * 1024 * 1024) { // 50MB limit
      setError('File size must be less than 50MB');
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  // Validate image
  const validateAndSetImage = (selectedFile) => {
    const validExtensions = ['.jpg', '.jpeg', '.png', '.bmp'];
    const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      setError('Please select a valid image file (.jpg, .jpeg, .png, .bmp)');
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
      setError('Image size must be less than 10MB');
      return;
    }

    setImageFile(selectedFile);
    setError(null);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      if (activeTab === 0) {
        validateAndSetImage(files[0]);
      } else {
        validateAndSetFile(files[0]);
      }
    }
  };

  // Process single image for style/pattern detection
  const processImage = async () => {
    if (!imageFile) {
      setError('Please select an image file');
      return;
    }

    setProcessing(true);
    setError(null);
    setSuccess(null);
    setStyleResult(null);

    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('productType', productType);

    try {
      const data = await apiCall(API_ENDPOINTS.PRODUCT_DETAILS_PROCESS_IMAGE, {
        method: 'POST',
        body: formData,
      });

      if (data.success) {
        setStyleResult(data.result);
        // Success shown in activity progress widget
      } else {
        setError(data.message || 'Failed to process image');
      }
    } catch (err) {
      setError(`Error processing image: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Process batch file for style/pattern detection
  const processBatchFile = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    // Reset all previous process state before starting new one
    downloadTriggeredRef.current = false;
    setStatus(null);
    setProcessId(null);
    processIdRef.current = null;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setProcessing(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('productType', productType);

    try {
      const data = await apiCall(API_ENDPOINTS.PRODUCT_DETAILS_PROCESS_BATCH, {
        method: 'POST',
        body: formData,
      });

      if (data.processId) {
        setProcessId(data.processId);
        processIdRef.current = data.processId;

        // Add to global activity log context
        addActivityProcess(data.processId, 'Style & Pattern Batch Processing', 'Product Details');

        // Add to process context
        addProcess({
          id: data.processId,
          name: 'Product Details Batch Processing',
          type: 'productDetails',
          status: 'processing',
          progress: 0,
        });
      } else {
        setError(data.message || 'Failed to start processing');
        setProcessing(false);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
      setProcessing(false);
    }
  };

  // Process colors
  const processColors = async (mode = null) => {
    if (!file) {
      setError('Please select a file with ItemIDs or Colors');
      return;
    }

    // Use passed mode or fall back to state
    const processingMode = mode || colorProcessingMode;

    // Reset all previous process state before starting new one
    downloadTriggeredRef.current = false;
    setStatus(null);
    setProcessId(null);
    processIdRef.current = null;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setProcessing(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', processingMode);

    try {
      const data = await apiCall(API_ENDPOINTS.PRODUCT_DETAILS_PROCESS_COLORS, {
        method: 'POST',
        body: formData,
      });

      if (data.processId) {
        setProcessId(data.processId);
        processIdRef.current = data.processId;
        setSuccess('Color processing started. This may take several minutes...');

        // Add to global activity log context
        addActivityProcess(data.processId, 'Color Family Processing', 'Product Details');

        // Add to process context
        addProcess({
          id: data.processId,
          name: 'Color Family Processing',
          type: 'colorFamily',
          status: 'processing',
          progress: 0,
        });
      } else {
        setError(data.message || 'Failed to start color processing');
        setProcessing(false);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
      setProcessing(false);
    }
  };

  // Fetch processing status
  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current || processId;
    if (!currentProcessId) return;

    try {
      const data = await apiCall(API_ENDPOINTS.PRODUCT_DETAILS_STATUS(currentProcessId), {
        method: 'GET',
      });

      if (data) {
        setStatus(data);

        // Get values - handle both camelCase and PascalCase
        const progressPct = data.progressPercentage ?? data.ProgressPercentage ?? 0;
        const totalItems = data.totalItems ?? data.TotalItems ?? 0;
        const processedItems = data.processedItems ?? data.ProcessedItems ?? 0;
        const logs = data.logs ?? data.Logs ?? [];
        const currentStatus = data.status ?? data.Status ?? '';

        // Update global activity log
        updateActivityProcess(currentProcessId, {
          progressPercentage: progressPct,
          totalItems: totalItems,
          processedItems: processedItems,
        });

        // Update logs in global context
        if (logs && logs.length > 0) {
          setActivityLogs(currentProcessId, logs);
        }

        // Update process context
        updateProcess(currentProcessId, {
          status: currentStatus,
          progress: progressPct,
        });

        if (currentStatus === 'completed') {
          setProcessing(false);

          // Clear interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          clearIntervalRef(currentProcessId);

          // Complete the activity process
          completeActivityProcess(currentProcessId, true);


          // Auto-download the result file and open in new tab
          const fileUrl = data.ResultFileUrl || data.resultFileUrl;
          const awsUrl = data.AwsUrl || data.awsUrl;
          if (fileUrl && !downloadTriggeredRef.current) {
            downloadTriggeredRef.current = true;
            if (awsUrl) {
              // Download via S3 URL
              const link = document.createElement('a');
              link.href = awsUrl;
              link.download = 'Product Details Result.xlsx';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              // Also open in new tab
              window.open(awsUrl, '_blank');
            } else {
              downloadResult(fileUrl);
            }
          }
          removeProcess(currentProcessId);
        } else if (currentStatus === 'error') {
          setProcessing(false);

          // Clear interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          clearIntervalRef(currentProcessId);

          // Complete the activity process with error
          completeActivityProcess(currentProcessId, false);

          setError('Processing failed. Check the activity log for details.');
          removeProcess(currentProcessId);
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  // Download result file
  const downloadResult = async (fileUrl) => {
    try {
      const response = await apiCall(API_ENDPOINTS.PRODUCT_DETAILS_DOWNLOAD(fileUrl), {
        method: 'GET',
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const timestamp = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') + '_' +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0') +
        String(now.getSeconds()).padStart(2, '0');
      const prefix = activeTab === 2 ? 'ColorFamily' : 'StylePattern';
      a.download = `${prefix}_${timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(`Error downloading file: ${err.message}`);
    }
  };

  // Download template
  const downloadTemplate = async (templateType) => {
    try {
      const response = await apiCall(API_ENDPOINTS.PRODUCT_DETAILS_TEMPLATE(templateType), {
        method: 'GET',
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      const timestamp = now.replace(/,/g, '').replace(/:/g, '-');
      const label = templateType === 'items' ? 'Style Pattern' : 'Colors';
      a.download = `Product Details ${label} Template ${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(`Error downloading template: ${err.message}`);
    }
  };

  // Clear all
  const clearAll = () => {
    setFile(null);
    setImageFile(null);
    setImagePreview(null);
    setStyleResult(null);
    setError(null);
    setSuccess(null);
    setProcessing(false);
    setProcessId(null);
    processIdRef.current = null;
    setStatus(null);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Product Details & Style Detection" category="excel" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          PRODUCT DETAILS & STYLE DETECTION
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(e, v) => {
            // Reset all process state when switching tabs to avoid stale state
            downloadTriggeredRef.current = false;
            setStatus(null);
            setProcessId(null);
            processIdRef.current = null;
            setProcessing(false);
            setError(null);
            setSuccess(null);
            setStyleResult(null);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            setActiveTab(v);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 3,
            '& .MuiTabs-indicator': {
              backgroundColor: TOOL_COLORS.OTHER.primary,
            },
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.9rem' },
              minHeight: 48,
              minWidth: { xs: 'auto', sm: 160 },
              '&.Mui-selected': {
                color: TOOL_COLORS.OTHER.primary,
              },
            }
          }}
        >
          <Tab label="Style & Pattern Detection" icon={<Style />} iconPosition="start" />
          <Tab label="Batch Processing" icon={<Description />} iconPosition="start" />
          <Tab label="Color Family Translation" icon={<ColorLens />} iconPosition="start" />
        </Tabs>

        <Grid container spacing={3}>
          {/* Left Column - Main Content */}
          <Grid item xs={12} md={8}>
            {/* Product Type Selection - Only show for Style & Pattern tabs */}
            {activeTab !== 2 && (
              <Card sx={{
                mb: 3,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa'
              }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    Product Type
                  </Typography>
                  <RadioGroup
                    row
                    value={productType}
                    onChange={(e) => setProductType(e.target.value)}
                  >
                    <FormControlLabel value="rugs" control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.OTHER.primary } }} />} label="Rugs" />
                    <FormControlLabel value="non-rugs" control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.OTHER.primary } }} />} label="Non-Rugs (Furniture)" />
                    {activeTab === 1 && (
                      <FormControlLabel value="sei-topline" control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.OTHER.primary } }} />} label="SEI / Topline" />
                    )}
                  </RadioGroup>
                </CardContent>
              </Card>
            )}

      {/* Tab 0: Single Image Processing */}
      {activeTab === 0 && (
        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            Process Single Image
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
            Upload an image to detect style and pattern (for rugs)
          </Typography>

          <Card sx={{
            mb: 3,
            bgcolor: imageFile ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ImageIcon sx={{ color: TOOL_COLORS.OTHER.primary }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Image Upload
                </Typography>
                {imageFile && (
                  <CheckCircle color="success" />
                )}
              </Box>

              <input type="file" ref={imageInputRef} onChange={handleImageSelect} accept=".jpg,.jpeg,.png,.bmp" style={{ display: 'none' }} />

              <Box
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => imageInputRef.current?.click()}
                sx={{
                  border: `2px dashed ${isDragging ? TOOL_COLORS.OTHER.primary : imageFile ? '#4CAF50' : theme.palette.divider}`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: isDragging
                    ? TOOL_COLORS.OTHER.light
                    : imageFile
                      ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                      : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  mb: 2,
                  '&:hover': {
                    borderColor: imageFile ? '#4CAF50' : TOOL_COLORS.OTHER.primary,
                    bgcolor: imageFile
                      ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                      : TOOL_COLORS.OTHER.light,
                  }
                }}
              >
                {!imageFile ? (
                  <>
                    <ImageIcon sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.OTHER.primary : 'text.secondary', mb: 1 }} />
                    <Typography variant="body1" gutterBottom>
                      {isDragging ? 'Drop image here' : 'Drag & drop image here or click to browse'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      JPG, JPEG, PNG, BMP (Max 10MB)
                    </Typography>
                  </>
                ) : (
                  <>
                    <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                    <Typography variant="body1" gutterBottom>1 file selected</Typography>
                    <Typography variant="caption" color="text.secondary">Click or drop to replace</Typography>
                  </>
                )}
              </Box>

              {imageFile && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  p: 1, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1, transition: 'all 0.2s ease', mb: 2,
                  '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', borderColor: TOOL_COLORS.OTHER.primary }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    <ImageIcon sx={{ color: TOOL_COLORS.OTHER.primary, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{imageFile.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>({(imageFile.size / 1024).toFixed(1)} KB)</Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setImageFile(null); if (imageInputRef.current) imageInputRef.current.value = ''; }} sx={{ color: theme.palette.error.main }}>
                    <Clear fontSize="small" />
                  </IconButton>
                </Box>
              )}

            </CardContent>
          </Card>

          {imagePreview && (
            <Card sx={{ mb: 3, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Image Preview
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<ZoomIn />}
                    onClick={() => setImageModalOpen(true)}
                    sx={{ color: TOOL_COLORS.OTHER.primary }}
                  >
                    View Full Size
                  </Button>
                </Box>
                <Box
                  sx={{
                    textAlign: 'center',
                    cursor: 'pointer',
                    position: 'relative',
                    '&:hover': {
                      opacity: 0.9,
                      '& .zoom-overlay': {
                        opacity: 1
                      }
                    }
                  }}
                  onClick={() => setImageModalOpen(true)}
                >
                  <img
                    src={imagePreview}
                    alt="Selected Image Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '400px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      border: `2px solid ${theme.palette.divider}`,
                      objectFit: 'contain'
                    }}
                  />
                  <Box
                    className="zoom-overlay"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <ZoomIn />
                    <Typography variant="body2">Click to enlarge</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Image Modal for Full Size View */}
          <Dialog
            open={imageModalOpen}
            onClose={() => setImageModalOpen(false)}
            maxWidth="lg"
            fullWidth
            slotProps={{
              paper: {
                sx: {
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  overflow: 'hidden'
                }
              }
            }}
          >
            <DialogContent sx={{ position: 'relative', p: 0, bgcolor: 'rgba(0, 0, 0, 0.9)' }}>
              <IconButton
                onClick={() => setImageModalOpen(false)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)'
                  },
                  zIndex: 1
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '80vh',
                  p: 4
                }}
              >
                <img
                  src={imagePreview}
                  alt="Full Size Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </DialogContent>
          </Dialog>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
              onClick={processImage}
              disabled={!imageFile || processing}
              sx={{
                bgcolor: TOOL_COLORS.OTHER.primary,
                '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover },
                px: 4,
                flex: 1
              }}
            >
              {processing ? 'Processing...' : 'Process Image'}
            </Button>
          </Box>

          {styleResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Results:</strong>
              </Typography>
              <Typography variant="body2">
                Style: <strong>{styleResult.style}</strong>
              </Typography>
              {productType === 'rugs' && styleResult.pattern && (
                <Typography variant="body2">
                  Pattern: <strong>{styleResult.pattern}</strong>
                </Typography>
              )}
              {styleResult.reasoning && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                  Reasoning: {styleResult.reasoning}
                </Typography>
              )}
            </Alert>
          )}
        </Paper>
      )}

      {/* Tab 1: Batch Processing */}
      {activeTab === 1 && (
        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            Batch Process Items
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
            Upload a file with ItemIDs to process multiple items
          </Typography>

          <Card sx={{
            mb: 3,
            bgcolor: file ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Description sx={{ color: TOOL_COLORS.OTHER.primary }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Import File (Excel/CSV)
                </Typography>
                {file && (
                  <CheckCircle color="success" />
                )}
              </Box>

              <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".xlsx,.xls,.csv" style={{ display: 'none' }} />

              <Box
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: `2px dashed ${isDragging ? TOOL_COLORS.OTHER.primary : file ? '#4CAF50' : theme.palette.divider}`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: isDragging
                    ? TOOL_COLORS.OTHER.light
                    : file
                      ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                      : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  mb: 2,
                  '&:hover': {
                    borderColor: file ? '#4CAF50' : TOOL_COLORS.OTHER.primary,
                    bgcolor: file
                      ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                      : TOOL_COLORS.OTHER.light,
                  }
                }}
              >
                {!file ? (
                  <>
                    <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.OTHER.primary : 'text.secondary', mb: 1 }} />
                    <Typography variant="body1" gutterBottom>
                      {isDragging ? 'Drop file here' : 'Drop your Excel or CSV file here or click to browse'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Excel (.xlsx, .xls) or CSV (.csv) files supported
                    </Typography>
                  </>
                ) : (
                  <>
                    <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                    <Typography variant="body1" gutterBottom>1 file selected</Typography>
                    <Typography variant="caption" color="text.secondary">Click or drop to replace</Typography>
                  </>
                )}
              </Box>

              {file && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  p: 1, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1, transition: 'all 0.2s ease', mb: 2,
                  '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', borderColor: TOOL_COLORS.OTHER.primary }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    <Description sx={{ color: TOOL_COLORS.OTHER.primary, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>({(file.size / 1024).toFixed(1)} KB)</Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} sx={{ color: theme.palette.error.main }}>
                    <Clear fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {false && file && (
                <Button variant="outlined" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} startIcon={<Clear />} sx={{ borderColor: theme.palette.divider }}>
                  Remove File
                </Button>
              )}
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => downloadTemplate('items')}
              sx={{
                borderColor: TOOL_COLORS.OTHER.primary,
                color: TOOL_COLORS.OTHER.primary,
                '&:hover': { borderColor: TOOL_COLORS.OTHER.hover, bgcolor: alpha(TOOL_COLORS.OTHER.primary, 0.08) },
                textTransform: 'none',
                fontWeight: 600,
                flex: { xs: '1 1 100%', sm: '0 1 auto' },
              }}
            >
              Download Template
            </Button>
            <Button
              variant="contained"
              startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
              onClick={processBatchFile}
              disabled={!file || processing}
              sx={{
                bgcolor: TOOL_COLORS.OTHER.primary,
                '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover },
                px: 4,
                flex: { xs: '1 1 100%', sm: '1 1 auto' }
              }}
            >
              {processing ? 'Processing...' : 'Process Batch'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Tab 2: Color Family Translation */}
      {activeTab === 2 && (
        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            Color Family Translation
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
            Translate colors to standardized color families
          </Typography>

          {/* File Upload Section - Shared across both modes */}
          <Card sx={{
            mb: 3,
            bgcolor: file ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ColorLens sx={{ color: TOOL_COLORS.OTHER.primary }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Import File (Excel/CSV)
                </Typography>
                {file && (
                  <CheckCircle color="success" />
                )}
              </Box>

              <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".xlsx,.xls,.csv" style={{ display: 'none' }} />

              <Box
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: `2px dashed ${isDragging ? TOOL_COLORS.OTHER.primary : file ? '#4CAF50' : theme.palette.divider}`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: isDragging
                    ? TOOL_COLORS.OTHER.light
                    : file
                      ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                      : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  mb: 2,
                  '&:hover': {
                    borderColor: file ? '#4CAF50' : TOOL_COLORS.OTHER.primary,
                    bgcolor: file
                      ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                      : TOOL_COLORS.OTHER.light,
                  }
                }}
              >
                {!file ? (
                  <>
                    <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.OTHER.primary : 'text.secondary', mb: 1 }} />
                    <Typography variant="body1" gutterBottom>
                      {isDragging ? 'Drop file here' : 'Upload file with ItemIDs or Colors'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Excel (.xlsx, .xls) or CSV (.csv) files supported
                    </Typography>
                  </>
                ) : (
                  <>
                    <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                    <Typography variant="body1" gutterBottom>1 file selected</Typography>
                    <Typography variant="caption" color="text.secondary">Click or drop to replace</Typography>
                  </>
                )}
              </Box>

              {file && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  p: 1, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1, transition: 'all 0.2s ease', mb: 2,
                  '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', borderColor: TOOL_COLORS.OTHER.primary }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    <Description sx={{ color: TOOL_COLORS.OTHER.primary, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>({(file.size / 1024).toFixed(1)} KB)</Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} sx={{ color: theme.palette.error.main }}>
                    <Clear fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {false && file && (
                <Button variant="outlined" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} startIcon={<Clear />} sx={{ borderColor: theme.palette.divider }}
                >
                  Remove File
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Two Sub-sections like Desktop */}
          <Grid container spacing={3}>
            {/* Section 1: By ItemID/WebID */}
            <Grid item xs={12} md={6}>
              <Card sx={{
                height: '100%',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
                border: `1px solid ${theme.palette.divider}`
              }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
                    By ItemID/WebID
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic', textAlign: 'center' }}>
                    Enter ItemID/WebID
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={() => downloadTemplate('colors')}
                      sx={{ borderColor: TOOL_COLORS.OTHER.primary, color: TOOL_COLORS.OTHER.primary, '&:hover': { borderColor: TOOL_COLORS.OTHER.hover, bgcolor: alpha(TOOL_COLORS.OTHER.primary, 0.08) }, textTransform: 'none', fontWeight: 600 }}
                      size="small"
                    >
                      Download Template
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={processing && colorProcessingMode === 'sku' ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
                      onClick={() => {
                        setColorProcessingMode('sku');
                        processColors('sku');
                      }}
                      disabled={!file || processing}
                      sx={{
                        bgcolor: TOOL_COLORS.OTHER.primary,
                        '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover },
                        fontSize: '0.8rem'
                      }}
                    >
                      {processing && colorProcessingMode === 'sku' ? 'Processing...' : 'Processing Through Items Color'}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={processing && colorProcessingMode === 'image' ? <CircularProgress size={16} color="inherit" /> : <ImageIcon />}
                      onClick={() => {
                        setColorProcessingMode('image');
                        processColors('image');
                      }}
                      disabled={!file || processing}
                      sx={{
                        bgcolor: TOOL_COLORS.OTHER.primary,
                        '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover },
                        fontSize: '0.8rem'
                      }}
                    >
                      {processing && colorProcessingMode === 'image' ? 'Processing...' : 'Processing Through Items Image'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Section 2: By Color */}
            <Grid item xs={12} md={6}>
              <Card sx={{
                height: '100%',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
                border: `1px solid ${theme.palette.divider}`
              }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
                    By Color
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic', textAlign: 'center' }}>
                    Enter Color/s
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={() => downloadTemplate('colors')}
                      sx={{ borderColor: TOOL_COLORS.OTHER.primary, color: TOOL_COLORS.OTHER.primary, '&:hover': { borderColor: TOOL_COLORS.OTHER.hover, bgcolor: alpha(TOOL_COLORS.OTHER.primary, 0.08) }, textTransform: 'none', fontWeight: 600 }}
                      size="small"
                    >
                      Download Template
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={processing && colorProcessingMode === 'color' ? <CircularProgress size={16} color="inherit" /> : <ColorLens />}
                      onClick={() => {
                        setColorProcessingMode('color');
                        processColors('color');
                      }}
                      disabled={!file || processing}
                      sx={{
                        bgcolor: TOOL_COLORS.OTHER.primary,
                        '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover },
                        fontSize: '0.8rem'
                      }}
                    >
                      {processing && colorProcessingMode === 'color' ? 'Processing...' : "Process Color Family's From Color Names"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}
          </Grid>

          {/* Right Column - Instructions */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50', position: 'sticky', top: 20 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
                INSTRUCTIONS:
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, fontSize: '0.8rem' }}>
                Style and Pattern Detection
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Select a Product Type and then import itemIDs or designs to receive their styles and patterns.
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                Style Image Detection
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Select a Product Type and then choose an image to receive the appropriate styling for it.
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                Family Color Translation by SKU
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Enter any itemID or webID to get their family color grouping. (Can enter rugs and non-rugs together)
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                Family Color Translation by Color
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Enter any color or color combination to get their family color grouping.
              </Typography>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>AI Prompts Used:</Typography>

              {/* Style & Pattern prompt - Rugs */}
              {activeTab !== 2 && productType === 'rugs' && (
                <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}`, mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Rug Style & Pattern Prompt</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.7, bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap' }}>
{`Analyze this rug image and identify its style and pattern.

STYLE - Choose ONLY ONE from Wayfair's pool of styles:
Modern, Mid-Century Modern, Traditional, Country / Farmhouse, Transitional Modern, Bohemian, Ultra-Modern, Contemporary Glam, Tropical, Classic Industrial, Coastal, Glam, Industrial, Retro, Rustic / Lodge, Scandinavian, Seasonal

PATTERN - Use one of Wayfair's patterns only.`}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Style prompt - SEI/Topline */}
              {activeTab !== 2 && productType === 'sei-topline' && (
                <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}`, mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>SEI / Topline Style Prompt</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.7, bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap' }}>
{`From the image, identify the furniture style.

You MUST choose exactly one of these styles:
• Industrial
• Transitional
• Rustic
• Farmhouse
• Modern
• Bohemian
• Mid-Century
• Traditional

Reply with only the style name.
No extra text or special characters.`}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Style prompt - Non-Rugs Furniture */}
              {activeTab !== 2 && productType === 'non-rugs' && (
                <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}`, mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Furniture Style Prompt</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.7, bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap' }}>
{`Analyze this furniture image and identify its style.

STYLE - Choose ONLY ONE from Wayfair's pool of styles:
Modern, Mid-Century Modern, Traditional, Country / Farmhouse, Transitional Modern, Bohemian, Ultra-Modern, Contemporary Glam, Tropical, Classic Industrial, Coastal, Glam, Industrial, Retro, Rustic / Lodge, Scandinavian, Seasonal`}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Color Family prompt */}
              {activeTab === 2 && (
                <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}`, mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Color Family Translation Prompt</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.7, bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap' }}>
{`Map each color to the closest match from this list:

Beige, Black, Blue, Bronze, Brown, Clear, Cream, Gold, Gray, Green, Multicolor, Off White, Orange, Pink, Purple, Red, Silver, White, Yellow, Teal, Navy, Unfinished.

Return ONLY a JSON array with color families in the same order.
Do not include markdown formatting or code blocks.`}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Paper>
          </Grid>
        </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      </Box>
    </Box>
  );
};

export default ProductDetails;